import Link from "next/link";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";

import {
  badgeStyles,
  buttonSizeStyles,
  buttonStyles,
  controlStyles,
  surfaceStyles,
  type BadgeVariant,
  type ButtonSize,
  type ButtonVariant,
  type SurfaceTone,
} from "./style-config";

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

export function Surface({
  tone = "default",
  className,
  children,
}: {
  tone?: SurfaceTone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={classNames(surfaceStyles[tone], className)}>{children}</div>
  );
}

type ActionBaseProps = {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
};

type ActionProps =
  | (ActionBaseProps &
      Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "href"> & {
        href: string;
      })
  | (ActionBaseProps &
      Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> & {
        href?: never;
      });

export function Action({
  children,
  variant = "primary",
  size = "medium",
  className,
  href,
  type = "button",
  ...buttonProps
}: ActionProps) {
  const classes = classNames(
    buttonStyles[variant],
    variant !== "text" && buttonSizeStyles[size],
    className,
  );

  if (href) {
    const linkProps = buttonProps as Omit<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      "className" | "href"
    >;

    return (
      <Link href={href} className={classes} {...linkProps}>
        {children}
      </Link>
    );
  }

  const actualButtonProps = buttonProps as Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "className"
  >;

  return (
    <button
      type={type as ButtonHTMLAttributes<HTMLButtonElement>["type"]}
      className={classes}
      {...actualButtonProps}
    >
      {children}
    </button>
  );
}

export function Badge({
  children,
  variant = "grain",
  className,
}: {
  children: ReactNode;
  variant?: BadgeVariant;
  className?: string;
}) {
  return (
    <span className={classNames(badgeStyles[variant], className)}>
      {children}
    </span>
  );
}

export function TagList({
  items,
  variant = "grain",
  className,
  label,
  limit,
}: {
  items: readonly string[];
  variant?: BadgeVariant;
  className?: string;
  label: string;
  limit?: number;
}) {
  return (
    <ul className={classNames("flex flex-wrap gap-2", className)} aria-label={label}>
      {items.slice(0, limit).map((item) => (
        <li key={item}>
          <Badge variant={variant}>{item}</Badge>
        </li>
      ))}
    </ul>
  );
}

export function Select({
  controlSize = "default",
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & {
  controlSize?: keyof typeof controlStyles.selectSize;
}) {
  return (
    <select
      className={classNames(
        controlStyles.select,
        controlStyles.selectSize[controlSize],
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
}

export { classNames };
